// Knowledge Centre — the reviewed corpus behind every grounded answer.
//
//   KnowledgeCentreView        /chatbot/knowledge-centre       ingest, corpus, review queues, retrieval preview
//   KnowledgeSourceDetailView  /chatbot/knowledge-centre/:id   one source: chunks, candidates, links, provenance
//
// The property these two screens exist to make visible: nothing from a source
// reaches a user until an operator approves a chunk, and an approved chunk that
// has gone stale is dropped from retrieval again. On a fraud-advice product a
// stale helpline number is worse than no answer, so "is this source actually
// reaching users" is a column, a badge and a banner — never something an
// operator has to infer from a status string.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from "react";

import { ApiError, http, qs } from "./api";
import { useSession } from "./App";
import {
  Badge,
  Banner,
  Confirm,
  CopyButton,
  Empty,
  Field,
  Num,
  RelativeTime,
  Search,
  SkeletonRows,
  Spinner,
  Table,
  Tabs,
  Tag,
  useAsync,
  useToast,
  type BadgeTone,
  type Column,
} from "./ui";
import { StatTile } from "./charts";

// ============================================================ API payloads

interface KnowledgeSource {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  uploaded_by_id?: string | null;
  source_type: string;
  title?: string | null;
  file_name?: string | null;
  mime_type?: string | null;
  file_size?: number | null;
  storage_key?: string | null;
  source_url?: string | null;
  status: string;
  review_status: string;
  /** The column is `list[Any]`; entries are rendered through String(). */
  tags?: unknown[] | null;
  raw_text_hash?: string | null;
  extraction_error?: string | null;
  chunk_count: number;
  metadata?: Record<string, unknown> | null;
  processed_at?: string | null;
}

interface KnowledgeChunk {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  knowledge_source_id: string;
  chunk_index: number;
  content: string;
  content_hash: string;
  token_estimate: number;
  status: string;
  review_status: string;
  linked_graph_node_id?: string | null;
  linked_wiki_source_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface KnowledgeCandidate {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  knowledge_source_id: string;
  knowledge_chunk_id?: string | null;
  candidate_type: string;
  target_kind?: string | null;
  target_key?: string | null;
  status: string;
  confidence: number;
  payload?: Record<string, unknown> | null;
  reviewer_note?: string | null;
  reviewed_by_id?: string | null;
  reviewed_at?: string | null;
}

interface AuditLogEntry {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  actor_id?: string | null;
  action: string;
  target_type: string;
  target_id?: string | null;
  diff?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

/** A chunk link as stored inside `chunk.metadata.links` (or the legacy `graph_links`). */
interface ChunkLink {
  id: string;
  link_type: string;
  target_kind?: string;
  target_key?: string;
  target_id?: string;
  label?: string;
  relation?: string;
  linked_at?: string;
}

interface SourceDetail {
  source: KnowledgeSource;
  chunks: KnowledgeChunk[];
  candidates: KnowledgeCandidate[];
  audit_logs: AuditLogEntry[];
  /** Flattened copy of every chunk's links; the chunk tab reads them per chunk. */
  graph_links?: unknown[];
}

interface SourceWriteResult {
  source: KnowledgeSource;
  /** False when the ingest bus is off and chunking ran inline (or not at all). */
  processing_enabled: boolean;
}

interface ChunkLinkResult {
  chunk: KnowledgeChunk;
  link: ChunkLink;
}

/** A row of the knowledge review queue: a chunk joined to its source. */
interface QueueChunk {
  id: string;
  knowledge_source_id: string;
  chunk_index: number;
  content: string;
  token_estimate: number;
  status: string;
  review_status: string;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  source_title?: string | null;
  source_type?: string | null;
  file_name?: string | null;
  source_url?: string | null;
}

interface QueueCandidate {
  id: string;
  knowledge_source_id: string;
  knowledge_chunk_id?: string | null;
  candidate_type: string;
  target_kind?: string | null;
  target_key?: string | null;
  status: string;
  confidence: number;
  payload?: Record<string, unknown> | null;
  created_at: string;
  source_title?: string | null;
  source_type?: string | null;
  file_name?: string | null;
  source_url?: string | null;
}

interface ReviewQueuePayload {
  chunks: QueueChunk[];
  candidates: QueueCandidate[];
}

/** An item of the trust review queue (`review_queue_items`). */
interface TrustItem {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  kind: string;
  source: string;
  target_type: string;
  target_id: string;
  status: string;
  severity: string;
  reason?: string | null;
  payload?: Record<string, unknown> | null;
  reviewer_note?: string | null;
  reviewed_by_id?: string | null;
  reviewed_at?: string | null;
}

interface PreviewChunk {
  id: string;
  knowledge_source_id: string;
  chunk_index: number;
  content: string;
  token_estimate: number;
  review_status: string;
  score: number;
  source_title?: string | null;
  source_type?: string | null;
}

interface PreviewPath {
  id?: string;
  title?: string;
  reason?: string;
  score?: number;
}

interface PreviewContextBlock {
  id?: string;
  title?: string;
  redacted_excerpt?: string;
  confidence?: number;
  freshness_status?: string;
  review_status?: string;
  why_selected?: string;
  user_facing_eligible?: boolean;
}

interface PreviewResult {
  chunks?: PreviewChunk[];
  graph_paths?: PreviewPath[];
  context_blocks?: PreviewContextBlock[];
  routing?: Record<string, string>;
  source_decision?: string;
  warnings?: string[];
  graph_backend?: string;
}

interface GraphResyncResult {
  backend?: string;
  accepted_chunks?: number;
  accepted_candidates?: number;
  linked_similar?: number;
  issues?: unknown[];
}

// ============================================================ small helpers

const BASE = "/chatbot/knowledge-centre";

/** The shell owns the URL and only re-reads it on popstate. */
function navigate(to: string): void {
  window.history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Screen-level error state. The trace id is the only handle support has. */
function ErrorBanner({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <Banner tone="critical" traceId={api?.traceId}>
      {what}: {errMessage(error, "request failed")}
    </Banner>
  );
}

function rec(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function numeric(value: unknown): number | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
}

function listOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function json(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function fmtBytes(value: number | null | undefined): string {
  if (!value || value <= 0) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function fmtPercent(value: unknown): string {
  const n = numeric(value);
  if (n === null || n === 0) return "";
  // Confidences arrive as 0..1 from the extractor and as 0..100 from some OCR
  // engines; both have to read as a percentage.
  return `${Math.round(n <= 1 ? n * 100 : n)}%`;
}

function daysSince(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

function titleOf(source: Pick<KnowledgeSource, "title" | "file_name" | "id">): string {
  return source.title?.trim() || source.file_name?.trim() || source.id;
}

// ---------------------------------------------------------------- statuses

// Same vocabulary the API validates against (KnowledgeReviewStatus /
// KnowledgeSourceStatus). Tones are the console's status roles, never a series
// colour, and every badge carries the word as well as the colour.
const STATUS_TONE: Record<string, BadgeTone> = {
  processed: "good",
  approved: "good",
  resolved: "good",
  queued: "warning",
  candidate: "warning",
  uploaded: "warning",
  chunked: "warning",
  needs_corroboration: "warning",
  needs_ocr: "warning",
  needs_review: "warning",
  rejected: "critical",
  publish_failed: "critical",
  extraction_failed: "critical",
  failed: "critical",
  suppressed: "neutral",
  merged: "neutral",
};

function statusTone(status: string): BadgeTone {
  return STATUS_TONE[(status || "").toLowerCase()] ?? "neutral";
}

function StatusBadge({ status, title }: { status: string | null | undefined; title?: string }) {
  const value = (status || "").trim();
  if (!value) return <span className="muted">—</span>;
  return (
    <Badge tone={statusTone(value)} title={title}>
      {value.replace(/_/g, " ")}
    </Badge>
  );
}

const SEVERITY_TONE: Record<string, BadgeTone> = {
  high: "critical",
  medium: "warning",
  low: "info",
};

// ---------------------------------------------------------------- freshness

// Age thresholds for the corpus. A source that has not been re-extracted in a
// quarter is treated as stale: the fraud landscape it describes moves faster
// than that, and the operator has to decide whether it still holds.
const AGEING_DAYS = 30;
const STALE_DAYS = 90;

type Freshness = "live" | "ageing" | "stale" | "excluded";

interface FreshnessVerdict {
  state: Freshness;
  label: string;
  tone: BadgeTone;
  /** Why, in one sentence. Rendered as the badge tooltip and in the detail banner. */
  why: string;
}

/**
 * What the corpus list can prove about one source reaching users.
 *
 * Retrieval reads chunks whose `review_status` is `approved`, so the list view
 * can only ever prove EXCLUSION (no chunks, failed extraction, OCR still
 * awaiting review). Everything else falls back to age. The detail view knows
 * the chunk-level truth and states it exactly.
 */
function sourceFreshness(source: KnowledgeSource): FreshnessVerdict {
  const status = (source.status || "").toLowerCase();
  const graph = rec(rec(source.metadata).knowledge_graph_sync);

  if (status === "extraction_failed" || status === "publish_failed") {
    return {
      state: "excluded",
      label: "Excluded",
      tone: "critical",
      why: "Extraction failed, so this source has no chunks and cannot ground an answer.",
    };
  }
  if (status === "needs_ocr" || status === "needs_review") {
    return {
      state: "excluded",
      label: "Excluded",
      tone: "warning",
      why: "OCR output is waiting on an operator; nothing here is approved yet.",
    };
  }
  if (!source.chunk_count) {
    return {
      state: "excluded",
      label: "Excluded",
      tone: "warning",
      why: "No chunks yet — nothing to approve and nothing to retrieve.",
    };
  }
  if (!source.processed_at) {
    return {
      state: "excluded",
      label: "Not extracted",
      tone: "warning",
      why: "The source has never finished extraction.",
    };
  }
  if (text(graph.status) === "failed") {
    return {
      state: "stale",
      label: "Graph stale",
      tone: "serious",
      why: `Graph sync failed${text(graph.fallback_reason) ? `: ${text(graph.fallback_reason)}` : ""}. Graph answers still use the last synced copy.`,
    };
  }

  const age = daysSince(source.processed_at);
  if (age !== null && age >= STALE_DAYS) {
    return {
      state: "stale",
      label: "Stale",
      tone: "serious",
      why: `Not re-extracted for ${age} days. Re-check it before trusting it in a user answer.`,
    };
  }
  if (age !== null && age >= AGEING_DAYS) {
    return {
      state: "ageing",
      label: "Ageing",
      tone: "warning",
      why: `Last extracted ${age} days ago.`,
    };
  }
  return {
    state: "live",
    label: "Live",
    tone: "good",
    why: "Recently extracted. Approved chunks from this source are eligible for retrieval.",
  };
}

interface ChunkFreshness {
  freshnessClass: string;
  lastValidated: string;
  /** True when the graph store will hide this chunk from user-facing retrieval. */
  stale: boolean;
}

/**
 * Directory facts (helplines, portals, bank numbers) carry a validation date.
 * The graph store hides a `directory` chunk with no `last_validated_on` from
 * retrieval entirely — that exclusion is the safety rule, so the operator has
 * to see it on the chunk rather than discover it from an empty answer.
 */
function chunkFreshness(metadata: Record<string, unknown> | null | undefined): ChunkFreshness {
  const fresh = rec(rec(metadata).freshness);
  const freshnessClass = text(fresh.freshness_class);
  const lastValidated = text(fresh.last_validated_on);
  return {
    freshnessClass,
    lastValidated,
    stale: freshnessClass === "directory" && !lastValidated.trim(),
  };
}

/** Candidates carry the same two keys flat in their payload. */
function candidateStale(payload: Record<string, unknown> | null | undefined): boolean {
  const p = rec(payload);
  return text(p.freshness_class) === "directory" && !text(p.last_validated_on).trim();
}

function duplicateIds(payload: Record<string, unknown> | null | undefined): string[] {
  const review = rec(rec(payload).operator_review);
  return listOf(review.duplicate_candidate_ids)
    .map((id) => text(id))
    .filter(Boolean);
}

function mergeTarget(id: string, payload: Record<string, unknown> | null | undefined): string {
  return duplicateIds(payload).find((other) => other !== id) ?? "";
}

function chunkLinks(metadata: Record<string, unknown> | null | undefined): ChunkLink[] {
  const meta = rec(metadata);
  const raw = meta.links === undefined ? meta.graph_links : meta.links;
  return listOf(raw).map((item) => {
    const link = rec(item);
    return {
      id: text(link.id),
      link_type: text(link.link_type),
      target_kind: text(link.target_kind),
      target_key: text(link.target_key),
      target_id: text(link.target_id),
      label: text(link.label),
      relation: text(link.relation),
      linked_at: text(link.linked_at),
    };
  });
}

// ============================================================ confirm plumbing

interface Pending {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  /** Must reject on failure — <Confirm> keeps the dialog open when it does. */
  run: () => Promise<void>;
}

function ConfirmHost({ pending, onClose }: { pending: Pending | null; onClose: () => void }) {
  return (
    <Confirm
      open={pending !== null}
      onClose={onClose}
      onConfirm={async () => {
        if (pending) await pending.run();
      }}
      title={pending?.title ?? ""}
      body={pending?.body}
      danger={pending?.danger}
      confirmLabel={pending?.confirmLabel}
    />
  );
}

// ============================================================ ingest panel

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

// Mirrors ALLOWED_UPLOAD_TYPES / EXTENSION_TYPES on the server. Kept here so a
// 40 MB file with an unsupported type is refused before it crosses the wire.
const ACCEPT_EXTENSIONS = [".txt", ".csv", ".pdf", ".docx", ".xls", ".xlsx", ".png", ".jpg", ".jpeg", ".webp", ".gif"];
const ACCEPT_TYPES = [
  "text/plain",
  "text/csv",
  "application/csv",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];
const ACCEPT_ATTR = [...ACCEPT_EXTENSIONS, ...ACCEPT_TYPES].join(",");

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
}

/** Returns the reason a file is refused, or "" when it is acceptable. */
function rejectReason(file: File): string {
  const ext = extensionOf(file.name);
  const typeOk = ACCEPT_TYPES.includes((file.type || "").split(";")[0].trim().toLowerCase());
  if (!typeOk && !ACCEPT_EXTENSIONS.includes(ext)) {
    return `File type not supported (${ext || file.type || "unknown"})`;
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `File is ${fmtBytes(file.size)}. The maximum is 50 MB`;
  }
  if (file.size === 0) return "File is empty";
  return "";
}

type UploadState = "queued" | "uploading" | "done" | "failed";

interface UploadItem {
  key: number;
  name: string;
  size: number;
  state: UploadState;
  note: string;
  sourceId?: string;
}

const UPLOAD_TONE: Record<UploadState, BadgeTone> = {
  queued: "neutral",
  uploading: "info",
  done: "good",
  failed: "critical",
};

interface IngestPanelProps {
  canWrite: boolean;
  onIngested: () => void;
}

/**
 * Upload (drag-drop or picker) and paste/URL creation.
 *
 * Files are uploaded one at a time and the meter tracks finished-of-queued.
 * That is the only progress figure that is actually true here: the shared HTTP
 * client uses fetch, which reports nothing until a request completes, and
 * reimplementing the upload on XHR would fork CSRF, org-header and 401-refresh
 * handling away from api.ts.
 */
function IngestPanel({ canWrite, onIngested }: IngestPanelProps) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [over, setOver] = useState(false);
  const [queue, setQueue] = useState<UploadItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadType, setUploadType] = useState("");
  const [uploadTags, setUploadTags] = useState("");

  const [sourceType, setSourceType] = useState("text");
  const [title, setTitle] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [extracted, setExtracted] = useState("");
  const [tags, setTags] = useState("");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");

  const done = queue.filter((q) => q.state === "done" || q.state === "failed").length;

  const upload = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      const accepted: Array<{ key: number; file: File }> = [];
      const seed: UploadItem[] = [];
      let key = Date.now();
      for (const file of files) {
        const reason = rejectReason(file);
        const item: UploadItem = {
          key: key++,
          name: file.name,
          size: file.size,
          state: reason ? "failed" : "queued",
          note: reason,
        };
        seed.push(item);
        // Keyed, not name-matched: two files dropped together can share a name.
        if (!reason) accepted.push({ key: item.key, file });
      }
      setQueue(seed);
      if (!accepted.length) {
        toast.push("No file was accepted. Check the type and size.", "critical");
        return;
      }

      // A single dropped file keeps the Vue behaviour of naming the source after
      // the file; a batch cannot share one title.
      const single = accepted.length === 1;
      setUploading(true);
      let lastId = "";
      let failures = 0;
      try {
        for (const entry of accepted) {
          setQueue((list) =>
            list.map((item) => (item.key === entry.key ? { ...item, state: "uploading" } : item))
          );
          const form = new FormData();
          form.append("file", entry.file);
          const stem = entry.file.name.replace(/\.[^.]+$/, "");
          form.append("title", single ? uploadTitle.trim() || stem : stem);
          if (uploadType) form.append("source_type", uploadType);
          if (uploadTags.trim()) form.append("tags", uploadTags.trim());
          form.append("metadata", JSON.stringify({ created_from: "knowledge_centre_upload" }));

          try {
            const result = await http.upload<SourceWriteResult>(`${BASE}/sources/upload`, form);
            lastId = result.source?.id ?? "";
            const note = result.source?.extraction_error
              ? `Uploaded with extraction note: ${result.source.extraction_error}`
              : result.processing_enabled
                ? "Queued for processing"
                : "Chunked locally";
            setQueue((list) =>
              list.map((item) =>
                item.key === entry.key ? { ...item, state: "done", note, sourceId: result.source?.id } : item
              )
            );
          } catch (err) {
            failures += 1;
            const note = errMessage(err, "Upload failed");
            setQueue((list) =>
              list.map((item) => (item.key === entry.key ? { ...item, state: "failed", note } : item))
            );
          }
        }
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }

      if (failures) toast.push(`${failures} of ${accepted.length} uploads failed`, "critical");
      else toast.push(accepted.length === 1 ? "Knowledge source uploaded" : `${accepted.length} sources uploaded`, "good");

      setUploadTitle("");
      onIngested();
      if (single && lastId) navigate(`${BASE}/${lastId}`);
    },
    [onIngested, toast, uploadTags, uploadTitle, uploadType]
  );

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;
    // Only fill the title from the filename when the operator has not typed one.
    if (files.length === 1 && !uploadTitle.trim()) setUploadTitle(files[0].name.replace(/\.[^.]+$/, ""));
    void upload(files);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    if (!canWrite) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length === 1 && !uploadTitle.trim()) setUploadTitle(files[0].name.replace(/\.[^.]+$/, ""));
    void upload(files);
  };

  const create = async () => {
    setFormError("");
    if (!title.trim()) {
      setFormError("Title is required");
      return;
    }
    if (!extracted.trim() && !sourceUrl.trim()) {
      setFormError("Add pasted text or a URL");
      return;
    }
    setCreating(true);
    try {
      const result = await http.post<SourceWriteResult>(`${BASE}/sources`, {
        source_type: sourceType,
        title: title.trim(),
        source_url: sourceUrl.trim() || undefined,
        extracted_text: extracted.trim() || undefined,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        metadata: { created_from: "knowledge_centre_admin" },
      });
      toast.push("Knowledge source created", "good");
      setSourceType("text");
      setTitle("");
      setSourceUrl("");
      setExtracted("");
      setTags("");
      onIngested();
      if (result.source?.id) navigate(`${BASE}/${result.source.id}`);
    } catch (err) {
      setFormError(errMessage(err, "Failed to create the knowledge source"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="section">
          <h4>Upload source</h4>
          <div
            className={`dropzone${over ? " over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (canWrite) setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
            onClick={() => canWrite && fileRef.current?.click()}
            role="button"
            tabIndex={canWrite ? 0 : -1}
            aria-disabled={!canWrite}
            onKeyDown={(e) => {
              if (canWrite && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                fileRef.current?.click();
              }
            }}
          >
            <span className="glyph" aria-hidden>
              ⇪
            </span>
            <span>
              {canWrite ? "Drop files here, or click to choose" : "You do not have permission to upload"}
            </span>
            <span className="muted">TXT, CSV, PDF, DOCX, XLS/XLSX, PNG, JPG, WEBP, GIF · up to 50 MB each</span>
          </div>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="sr-only"
            disabled={!canWrite || uploading}
            onChange={onPick}
          />

          {queue.length > 0 && (
            <div style={{ marginTop: "var(--sp-3)" }}>
              <div className="row" style={{ marginBottom: 4 }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  {done} of {queue.length} finished
                </span>
                <span className="spacer" />
                {uploading && <Spinner label="Uploading" />}
              </div>
              <div className="meter" aria-hidden>
                <span style={{ width: `${queue.length ? Math.round((done / queue.length) * 100) : 0}%` }} />
              </div>
              <div style={{ marginTop: "var(--sp-2)" }}>
                {queue.map((item) => (
                  <div className="upload-row" key={item.key}>
                    <span className="name" title={item.name}>
                      {item.name}
                    </span>
                    <span className="muted nowrap">{fmtBytes(item.size)}</span>
                    <Badge tone={UPLOAD_TONE[item.state]} title={item.note}>
                      {item.state}
                    </Badge>
                    {item.sourceId && (
                      <a className="btn sm ghost" href={`${BASE}/${item.sourceId}`}>
                        Open
                      </a>
                    )}
                  </div>
                ))}
                {queue.some((q) => q.note) && (
                  <p className="muted" style={{ fontSize: 12, marginTop: "var(--sp-2)" }}>
                    {queue.filter((q) => q.note).map((q) => `${q.name}: ${q.note}`).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          )}

          <div style={{ marginTop: "var(--sp-4)" }}>
            <Field label="Upload title" hint="Used for a single file. A batch is named after each filename.">
              <input
                type="text"
                value={uploadTitle}
                disabled={!canWrite}
                placeholder="Title"
                onChange={(e) => setUploadTitle(e.target.value)}
              />
            </Field>
            <Field label="Source type">
              <select value={uploadType} disabled={!canWrite} onChange={(e) => setUploadType(e.target.value)}>
                <option value="">Auto source type</option>
                <option value="sop">SOP</option>
                <option value="playbook">Playbook</option>
                <option value="image">Image / screenshot</option>
                <option value="file">Document</option>
              </select>
            </Field>
            <Field label="Tags" hint="Comma separated.">
              <input
                type="text"
                value={uploadTags}
                disabled={!canWrite}
                placeholder="recovery, upi, helpline"
                onChange={(e) => setUploadTags(e.target.value)}
              />
            </Field>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="section">
          <h4>Paste or URL</h4>
          {formError && (
            <Banner tone="warning" onDismiss={() => setFormError("")}>
              {formError}
            </Banner>
          )}
          <Field label="Source type">
            <select value={sourceType} disabled={!canWrite} onChange={(e) => setSourceType(e.target.value)}>
              <option value="text">Text</option>
              <option value="url">URL</option>
              <option value="sop">SOP</option>
              <option value="playbook">Playbook</option>
            </select>
          </Field>
          <Field label="Title" required>
            <input
              type="text"
              value={title}
              disabled={!canWrite}
              placeholder="Title"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          {sourceType === "url" && (
            <Field label="Source URL">
              <input
                type="url"
                value={sourceUrl}
                disabled={!canWrite}
                placeholder="https://…"
                onChange={(e) => setSourceUrl(e.target.value)}
              />
            </Field>
          )}
          <Field label="Text">
            <textarea
              rows={8}
              value={extracted}
              disabled={!canWrite}
              placeholder="Paste source text or SOP content"
              onChange={(e) => setExtracted(e.target.value)}
            />
          </Field>
          <Field label="Tags" hint="Comma separated.">
            <input
              type="text"
              value={tags}
              disabled={!canWrite}
              placeholder="recovery, upi"
              onChange={(e) => setTags(e.target.value)}
            />
          </Field>
          <button className="btn primary block" onClick={() => void create()} disabled={!canWrite || creating}>
            {creating && <span className="spinner" />}
            Create source
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================ retrieval preview

const WARNING_TEXT: Record<string, string> = {
  non_approved_or_stale_graph_context:
    "The graph returned context that is not approved or not fresh. It is kept out of user answers.",
  graph_context_not_user_facing: "The graph returned context flagged as not user-facing.",
  graph_preview_unavailable: "The graph is unreachable, so this preview shows approved chunks only.",
};

/**
 * Runs the same approved-only retrieval a user turn would run. `highlightId`
 * marks the hits belonging to the source being inspected, which is how an
 * operator answers "did my approval actually change what a user gets".
 */
function PreviewPanel({ highlightId }: { highlightId?: string }) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<PreviewResult | null>(null);

  const run = async () => {
    if (!query.trim()) {
      setError(new ApiError(0, "client_error", "Enter a preview query", ""));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setResult(await http.post<PreviewResult>(`${BASE}/retrieve-preview`, { query: query.trim(), limit: 5 }));
    } catch (err) {
      setError(err);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const routing = result?.routing ?? {};
  const routingRows: Array<[string, string]> = [
    ["Wiki", text(routing.wiki_source_id)],
    ["Playbook", text(routing.recovery_playbook_id)],
    ["Flow", text(routing.guided_flow_id)],
    ["Step", text(routing.flow_step_id)],
  ].filter((row): row is [string, string] => Boolean(row[1]));

  return (
    <div className="card">
      <div className="section">
        <h4>Approved retrieval preview</h4>
        <Field label="Question" hint="Only approved, fresh material can appear here.">
          <textarea
            rows={3}
            value={query}
            placeholder="Ask a recovery question to preview what a user would get"
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
        <button className="btn block" onClick={() => void run()} disabled={busy}>
          {busy && <span className="spinner" />}
          Preview
        </button>

        <ErrorBanner error={error} what="Preview failed" />

        {result && (
          <div style={{ marginTop: "var(--sp-3)" }}>
            <div className="row" style={{ marginBottom: "var(--sp-2)" }}>
              <span className="muted" style={{ fontSize: 12 }}>
                Source decision
              </span>
              <span className="spacer" />
              <Badge tone={result.source_decision === "no_grounding" ? "warning" : "info"}>
                {result.source_decision || "unknown"}
              </Badge>
            </div>

            <dl className="kv">
              {routingRows.map(([label, value]) => (
                <div key={label} style={{ display: "contents" }}>
                  <dt>{label}</dt>
                  <dd className="mono">{value}</dd>
                </div>
              ))}
              {routingRows.length === 0 && (
                <div style={{ display: "contents" }}>
                  <dt>Routing</dt>
                  <dd className="muted">No approved routing link selected</dd>
                </div>
              )}
              {result.graph_backend && (
                <div style={{ display: "contents" }}>
                  <dt>Graph</dt>
                  <dd>{result.graph_backend}</dd>
                </div>
              )}
            </dl>

            {listOf(result.warnings).length > 0 && (
              <div style={{ marginTop: "var(--sp-3)" }}>
                {(result.warnings ?? []).map((warning) => (
                  <Banner tone="warning" key={warning}>
                    {WARNING_TEXT[warning] ?? warning}
                  </Banner>
                ))}
              </div>
            )}

            {(result.graph_paths ?? []).length > 0 && (
              <div className="section" style={{ marginTop: "var(--sp-3)" }}>
                <h4>Graph paths</h4>
                {(result.graph_paths ?? []).map((path, i) => (
                  <div className="card" key={path.id || path.title || i} style={{ marginBottom: "var(--sp-2)" }}>
                    <div className="row">
                      <span className="strong truncate">{path.title || path.reason || path.id || "Graph path"}</span>
                      <span className="spacer" />
                      {path.score !== undefined && <Badge>score {Number(path.score).toFixed(2)}</Badge>}
                    </div>
                    {path.reason && (
                      <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>
                        {path.reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(result.context_blocks ?? []).length > 0 && (
              <div className="section">
                <h4>Graph context</h4>
                {(result.context_blocks ?? []).map((block, i) => {
                  const freshness = (block.freshness_status || "").toLowerCase();
                  const notFresh = Boolean(freshness) && freshness !== "fresh";
                  return (
                    <div className="card" key={block.id || block.title || i} style={{ marginBottom: "var(--sp-2)" }}>
                      <div className="chips">
                        <span className="strong truncate">{block.title || block.id || "Context"}</span>
                        <span className="spacer" />
                        {block.review_status && <StatusBadge status={block.review_status} />}
                        {block.freshness_status && (
                          <Badge
                            tone={notFresh ? "warning" : "good"}
                            title={
                              notFresh
                                ? "Not fresh — excluded from user-facing answers until revalidated."
                                : "Fresh enough to be quoted to a user."
                            }
                          >
                            {block.freshness_status}
                          </Badge>
                        )}
                        {block.user_facing_eligible === false && <Badge tone="critical">not user-facing</Badge>}
                      </div>
                      {block.redacted_excerpt && (
                        <p className="prewrap" style={{ fontSize: 12, margin: "6px 0 0" }}>
                          {block.redacted_excerpt}
                        </p>
                      )}
                      <div className="chips" style={{ marginTop: "var(--sp-2)" }}>
                        {block.confidence !== undefined && (
                          <span className="muted" style={{ fontSize: 11 }}>
                            confidence {Number(block.confidence).toFixed(2)}
                          </span>
                        )}
                        {block.why_selected && (
                          <span className="muted" style={{ fontSize: 11 }}>
                            {block.why_selected}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="section">
              <h4>Approved chunks</h4>
              {(result.chunks ?? []).length === 0 ? (
                <Empty title="No approved chunk matched" body="Nothing in the corpus can currently ground this question." />
              ) : (
                (result.chunks ?? []).map((chunk) => (
                  <a
                    className="card"
                    key={chunk.id}
                    href={`${BASE}/${chunk.knowledge_source_id}`}
                    style={{ display: "block", marginBottom: "var(--sp-2)", textDecoration: "none" }}
                  >
                    <div className="chips">
                      <span className="strong truncate">{chunk.source_title || chunk.knowledge_source_id}</span>
                      <span className="spacer" />
                      {highlightId === chunk.knowledge_source_id && <Badge tone="info">this source</Badge>}
                      <Badge>score {chunk.score}</Badge>
                    </div>
                    <p className="prewrap muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
                      {chunk.content.slice(0, 320)}
                      {chunk.content.length > 320 ? "…" : ""}
                    </p>
                  </a>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================ review actions

const CHUNK_ACTIONS: Array<{ label: string; status: string }> = [
  { label: "Approve", status: "approved" },
  { label: "Reject", status: "rejected" },
  { label: "Suppress", status: "suppressed" },
];

const CANDIDATE_ACTIONS: Array<{ label: string; status: string }> = [
  { label: "Approve", status: "approved" },
  { label: "Needs source", status: "needs_corroboration" },
  { label: "Reject", status: "rejected" },
  { label: "Suppress", status: "suppressed" },
];

function ReviewButtons({
  actions,
  busy,
  disabled,
  onPick,
}: {
  actions: Array<{ label: string; status: string }>;
  busy: boolean;
  disabled: boolean;
  onPick: (status: string) => void;
}) {
  return (
    <div className="chips">
      {busy && <Spinner label="Saving review" />}
      {actions.map((action) => (
        <button
          key={action.status}
          className={`btn sm${action.status === "approved" ? " primary" : ""}`}
          disabled={disabled || busy}
          onClick={() => onPick(action.status)}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

function NoteBox({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <textarea
      rows={2}
      value={value}
      disabled={disabled}
      placeholder="Reviewer note (kept in the audit trail)"
      style={{ marginTop: "var(--sp-3)" }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ============================================================ review queue

interface ReviewQueuePanelProps {
  canWrite: boolean;
  onChanged: () => void;
}

/** Chunks and candidates waiting on a human decision, across every source. */
function ReviewQueuePanel({ canWrite, onChanged }: ReviewQueuePanelProps) {
  const toast = useToast();
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("candidate");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);

  const queue = useAsync<ReviewQueuePayload>(
    () => http.get<ReviewQueuePayload>(`${BASE}/review-queue${qs({ kind, status })}`),
    [kind, status]
  );

  const chunks = queue.data?.chunks ?? [];
  const candidates = queue.data?.candidates ?? [];

  const reviewChunk = async (id: string, next: string) => {
    setBusyId(id);
    try {
      await http.post(`${BASE}/chunks/${id}/review`, { status: next, note: notes[id] || "" });
      toast.push("Chunk review saved", "good");
      queue.reload();
      onChanged();
    } catch (err) {
      toast.push(errMessage(err, "Failed to review the chunk"), "critical");
      throw err;
    } finally {
      setBusyId("");
    }
  };

  const reviewCandidate = async (id: string, next: string, mergeInto?: string) => {
    setBusyId(id);
    try {
      await http.post(`${BASE}/candidates/${id}/review`, {
        status: next,
        note: notes[id] || "",
        merge_into_candidate_id: mergeInto ?? "",
      });
      toast.push("Candidate review saved", "good");
      queue.reload();
      onChanged();
    } catch (err) {
      toast.push(errMessage(err, "Failed to review the candidate"), "critical");
      throw err;
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="card flush">
      <div className="card-head">
        <h3>Review queue</h3>
        <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 150 }} aria-label="Queue kind">
          <option value="all">Chunks and candidates</option>
          <option value="chunks">Chunks only</option>
          <option value="candidates">Candidates only</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 170 }} aria-label="Review status">
          <option value="candidate">Awaiting review</option>
          <option value="needs_corroboration">Needs corroboration</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="suppressed">Suppressed</option>
          <option value="merged">Merged</option>
        </select>
        <Badge>
          {chunks.length} chunks / {candidates.length} candidates
        </Badge>
        <button className="btn sm" onClick={queue.reload} disabled={queue.loading}>
          Refresh
        </button>
      </div>

      <div className="card-body">
        <ErrorBanner error={queue.error} what="Failed to load the review queue" />

        {queue.loading ? (
          <SkeletonRows n={4} cols={3} />
        ) : chunks.length === 0 && candidates.length === 0 ? (
          <Empty
            title="No pending review items"
            body="Nothing is waiting on a decision for this filter."
            icon="✓"
          />
        ) : (
          <>
            {chunks.map((chunk) => {
              const fresh = chunkFreshness(chunk.metadata);
              return (
                <div className="card" key={chunk.id} style={{ marginBottom: "var(--sp-3)" }}>
                  <div className="chips">
                    <Badge>chunk #{chunk.chunk_index}</Badge>
                    <StatusBadge status={chunk.review_status} />
                    {fresh.stale && (
                      <Badge
                        tone="warning"
                        title="Directory fact with no validation date. Even once approved, retrieval hides it."
                      >
                        stale directory fact
                      </Badge>
                    )}
                    <span className="muted" style={{ fontSize: 12 }}>
                      {chunk.token_estimate} tokens
                    </span>
                    <span className="spacer" />
                    <ReviewButtons
                      actions={CHUNK_ACTIONS}
                      busy={busyId === chunk.id}
                      disabled={!canWrite}
                      onPick={(next) => void reviewChunk(chunk.id, next).catch(() => undefined)}
                    />
                  </div>
                  <a href={`${BASE}/${chunk.knowledge_source_id}`} className="strong" style={{ display: "block", marginTop: "var(--sp-2)" }}>
                    {chunk.source_title || chunk.knowledge_source_id}
                  </a>
                  <div className="chips" style={{ marginTop: 4 }}>
                    {chunk.source_type && <Tag label={chunk.source_type} />}
                    {chunk.file_name && <span className="muted" style={{ fontSize: 11 }}>{chunk.file_name}</span>}
                    {chunk.source_url && (
                      <span className="muted truncate" style={{ fontSize: 11, maxWidth: 320 }}>
                        {chunk.source_url}
                      </span>
                    )}
                  </div>
                  <p className="prewrap" style={{ marginTop: "var(--sp-2)" }}>
                    {chunk.content}
                  </p>
                  <NoteBox
                    value={notes[chunk.id] ?? ""}
                    disabled={!canWrite}
                    onChange={(v) => setNotes((n) => ({ ...n, [chunk.id]: v }))}
                  />
                </div>
              );
            })}

            {candidates.map((candidate) => {
              const duplicates = duplicateIds(candidate.payload);
              const target = mergeTarget(candidate.id, candidate.payload);
              return (
                <div className="card" key={candidate.id} style={{ marginBottom: "var(--sp-3)" }}>
                  <div className="chips">
                    <Badge>{candidate.candidate_type}</Badge>
                    <StatusBadge status={candidate.status} />
                    {duplicates.length > 1 && <Badge tone="info">{duplicates.length} duplicates</Badge>}
                    {candidateStale(candidate.payload) && (
                      <Badge tone="warning" title="Directory fact with no validation date; retrieval hides it until it is validated.">
                        stale directory fact
                      </Badge>
                    )}
                    <span className="muted" style={{ fontSize: 12 }}>
                      confidence {Number(candidate.confidence || 0).toFixed(2)}
                    </span>
                    <span className="spacer" />
                    <ReviewButtons
                      actions={CANDIDATE_ACTIONS}
                      busy={busyId === candidate.id}
                      disabled={!canWrite}
                      onPick={(next) => void reviewCandidate(candidate.id, next).catch(() => undefined)}
                    />
                    {target && (
                      <button
                        className="btn sm"
                        disabled={!canWrite || busyId === candidate.id}
                        onClick={() =>
                          setPending({
                            title: "Merge duplicate candidate",
                            body: `This candidate is folded into ${target} and stops being reviewed on its own. The merge is recorded in the payload and cannot be undone from the console.`,
                            confirmLabel: "Merge",
                            run: async () => {
                              await reviewCandidate(candidate.id, "merged", target);
                            },
                          })
                        }
                      >
                        Merge duplicate
                      </button>
                    )}
                  </div>
                  <a href={`${BASE}/${candidate.knowledge_source_id}`} className="strong" style={{ display: "block", marginTop: "var(--sp-2)" }}>
                    {candidate.source_title || candidate.knowledge_source_id} / {candidate.target_kind || "candidate"} /{" "}
                    {candidate.target_key || "unkeyed"}
                  </a>
                  <pre className="pre-block">{json(candidate.payload)}</pre>
                  <NoteBox
                    value={notes[candidate.id] ?? ""}
                    disabled={!canWrite}
                    onChange={(v) => setNotes((n) => ({ ...n, [candidate.id]: v }))}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>

      <ConfirmHost pending={pending} onClose={() => setPending(null)} />
    </div>
  );
}

// ============================================================ trust queue

const TRUST_ACTIONS: Array<{ label: string; status: string }> = [
  { label: "Approve", status: "approved" },
  { label: "Resolved", status: "resolved" },
  { label: "Needs source", status: "needs_corroboration" },
  { label: "Reject", status: "rejected" },
  { label: "Suppress", status: "suppressed" },
];

const TRUST_KIND_LABEL: Record<string, string> = {
  stale_directory_fact: "Stale directory fact",
  graph_duplicate: "Graph duplicate",
  retrieval_miss: "Retrieval miss",
  low_confidence_ocr: "Low-confidence OCR",
  graph_sync_issue: "Graph sync issue",
  guardrail_block: "Guardrail block",
};

/**
 * The trust review queue: work the pipeline raised about itself.
 *
 * `stale_directory_fact` is the one that matters most — those graph nodes are
 * invisible to retrieval until a human revalidates them, and approving the item
 * is what clears the staleness upstream.
 */
function TrustQueuePanel({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("candidate");
  const [severity, setSeverity] = useState("all");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const queue = useAsync<{ items: TrustItem[] }>(
    () => http.get<{ items: TrustItem[] }>(`/chatbot/trust-review-queue${qs({ kind, status, severity })}`),
    [kind, status, severity]
  );

  const items = queue.data?.items ?? [];
  const stale = items.filter((i) => i.kind === "stale_directory_fact").length;

  const review = async (item: TrustItem, next: string) => {
    setBusyId(item.id);
    try {
      await http.post(`/chatbot/trust-review-queue/${item.id}/review`, {
        status: next,
        note: notes[item.id] || "",
      });
      toast.push("Review saved", "good");
      queue.reload();
    } catch (err) {
      toast.push(errMessage(err, "Failed to save the review"), "critical");
    } finally {
      setBusyId("");
    }
  };

  const columns: Column<TrustItem>[] = [
    {
      key: "kind",
      header: "Item",
      render: (item) => (
        <div>
          <div className="strong">{TRUST_KIND_LABEL[item.kind] ?? item.kind}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {item.reason || item.source}
          </div>
          <div className="chips" style={{ marginTop: 4 }}>
            <Tag label={item.target_type} />
            <span className="mono muted" style={{ fontSize: 11 }}>
              {item.target_id}
            </span>
            <CopyButton value={item.target_id} label="Copy target id" />
          </div>
        </div>
      ),
    },
    {
      key: "severity",
      header: "Severity",
      sortable: true,
      render: (item) => <Badge tone={SEVERITY_TONE[item.severity] ?? "neutral"}>{item.severity}</Badge>,
    },
    { key: "status", header: "Status", sortable: true, render: (item) => <StatusBadge status={item.status} /> },
    {
      key: "created_at",
      header: "Raised",
      sortable: true,
      render: (item) => <RelativeTime value={item.created_at} />,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (item) => (
        <button className="btn sm" onClick={() => setOpen(open === item.id ? null : item.id)}>
          {open === item.id ? "Hide" : "Review"}
        </button>
      ),
    },
  ];

  return (
    <div className="card flush">
      <div className="card-head">
        <h3>Trust review queue</h3>
        <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 190 }} aria-label="Item kind">
          <option value="all">All kinds</option>
          {Object.entries(TRUST_KIND_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 170 }} aria-label="Item status">
          <option value="candidate">Open</option>
          <option value="all">Any status</option>
          <option value="approved">Approved</option>
          <option value="resolved">Resolved</option>
          <option value="needs_corroboration">Needs corroboration</option>
          <option value="rejected">Rejected</option>
          <option value="suppressed">Suppressed</option>
        </select>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={{ width: 140 }} aria-label="Severity">
          <option value="all">Any severity</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <button className="btn sm" onClick={queue.reload} disabled={queue.loading}>
          Refresh
        </button>
      </div>

      <div className="card-body">
        <ErrorBanner error={queue.error} what="Failed to load the trust review queue" />
        {stale > 0 && (
          <Banner tone="warning">
            {stale} stale directory {stale === 1 ? "fact is" : "facts are"} hidden from user answers until someone
            revalidates {stale === 1 ? "it" : "them"}. Approving the item clears the staleness in the graph.
          </Banner>
        )}
      </div>

      <Table
        columns={columns}
        rows={items}
        loading={queue.loading}
        empty="Nothing in the trust review queue"
      />

      {open !== null &&
        items
          .filter((item) => item.id === open)
          .map((item) => (
            <div className="card-body" key={item.id} style={{ borderTop: "1px solid var(--separator)" }}>
              <h4>{TRUST_KIND_LABEL[item.kind] ?? item.kind}</h4>
              <dl className="kv" style={{ marginTop: "var(--sp-2)" }}>
                <dt>Raised by</dt>
                <dd>{item.source}</dd>
                <dt>Target</dt>
                <dd className="mono">
                  {item.target_type} · {item.target_id}
                </dd>
                <dt>Reviewed</dt>
                <dd>{item.reviewed_at ? fmtDate(item.reviewed_at) : "not yet"}</dd>
                {item.reviewer_note && (
                  <>
                    <dt>Note</dt>
                    <dd>{item.reviewer_note}</dd>
                  </>
                )}
              </dl>
              <pre className="pre-block">{json(item.payload)}</pre>
              <NoteBox
                value={notes[item.id] ?? ""}
                disabled={!canWrite}
                onChange={(v) => setNotes((n) => ({ ...n, [item.id]: v }))}
              />
              <div style={{ marginTop: "var(--sp-2)" }}>
                <ReviewButtons
                  actions={TRUST_ACTIONS}
                  busy={busyId === item.id}
                  disabled={!canWrite}
                  onPick={(next) => void review(item, next)}
                />
              </div>
            </div>
          ))}
    </div>
  );
}

// ============================================================ centre view

const FRESHNESS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "all", label: "Any freshness" },
  { value: "live", label: "Live" },
  { value: "ageing", label: "Ageing" },
  { value: "stale", label: "Stale" },
  { value: "excluded", label: "Excluded from retrieval" },
];

const SOURCE_STATUS_FILTERS = [
  "uploaded",
  "queued",
  "chunked",
  "processed",
  "needs_ocr",
  "needs_review",
  "extraction_failed",
  "publish_failed",
];

export function KnowledgeCentreView() {
  const { can } = useSession();
  const canWrite = can("settings.chatbot", "write");
  const toast = useToast();

  const [tab, setTab] = useState("sources");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [freshness, setFreshness] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [reviewNonce, setReviewNonce] = useState(0);

  const sources = useAsync<KnowledgeSource[]>(() => http.get<KnowledgeSource[]>(`${BASE}/sources`), []);
  const rows = useMemo(() => (Array.isArray(sources.data) ? sources.data : []), [sources.data]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((source) => {
      if (status !== "all" && source.status !== status) return false;
      if (freshness !== "all" && sourceFreshness(source).state !== freshness) return false;
      if (!needle) return true;
      return [
        source.title,
        source.source_type,
        source.status,
        source.review_status,
        source.source_url,
        source.file_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [rows, query, status, freshness]);

  // The headline safety number: how much of the corpus cannot reach a user.
  const blocked = useMemo(() => {
    let excluded = 0;
    let stale = 0;
    for (const source of rows) {
      const state = sourceFreshness(source).state;
      if (state === "excluded") excluded += 1;
      else if (state === "stale") stale += 1;
    }
    return { excluded, stale };
  }, [rows]);

  const resyncAll = async () => {
    setSyncing(true);
    try {
      const result = await http.post<GraphResyncResult>(`${BASE}/graph/resync`, {});
      const accepted = result.accepted_chunks ?? 0;
      toast.push(`Graph resync finished · ${accepted} chunks accepted`, "good");
      sources.reload();
    } catch (err) {
      toast.push(errMessage(err, "Failed to resync the knowledge graph"), "critical");
      // A failed resync is still recorded on every source, so the list has to
      // be re-read to show it.
      sources.reload();
    } finally {
      setSyncing(false);
    }
  };

  const columns: Column<KnowledgeSource>[] = [
    {
      key: "title",
      header: "Source",
      sortable: true,
      render: (source) => {
        const extraction = rec(rec(source.metadata).extraction);
        const ocr = fmtPercent(extraction.ocr_confidence);
        return (
          <div>
            <div className="strong">{titleOf(source)}</div>
            <div className="chips" style={{ marginTop: 4 }}>
              <Tag label={source.source_type} />
              {source.file_name && (
                <span className="muted" style={{ fontSize: 11 }}>
                  {source.file_name}
                </span>
              )}
              {source.file_size ? (
                <span className="muted" style={{ fontSize: 11 }}>
                  {fmtBytes(source.file_size)}
                </span>
              ) : null}
              {source.source_url && (
                <span className="muted truncate" style={{ fontSize: 11, maxWidth: 280 }} title={source.source_url}>
                  {source.source_url}
                </span>
              )}
            </div>
            {source.extraction_error && (
              <div className="truncate" style={{ color: "var(--critical)", fontSize: 12, maxWidth: 460, marginTop: 4 }}>
                {source.extraction_error}
              </div>
            )}
            {Boolean(ocr || extraction.status || extraction.review_reason) && (
              <div className="chips" style={{ marginTop: 4 }}>
                {ocr && (
                  <Badge tone={(numeric(extraction.ocr_confidence) ?? 1) < 0.7 ? "warning" : "neutral"}>OCR {ocr}</Badge>
                )}
                {extraction.status ? <StatusBadge status={text(extraction.status)} /> : null}
                {extraction.review_reason ? (
                  <span style={{ color: "var(--warning)", fontSize: 11 }}>{text(extraction.review_reason)}</span>
                ) : null}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "freshness",
      header: "Retrieval",
      render: (source) => {
        const verdict = sourceFreshness(source);
        return (
          <Badge tone={verdict.tone} title={verdict.why}>
            {verdict.label}
          </Badge>
        );
      },
    },
    { key: "status", header: "Status", sortable: true, render: (source) => <StatusBadge status={source.status} /> },
    {
      key: "review_status",
      header: "Review",
      sortable: true,
      render: (source) => <StatusBadge status={source.review_status} />,
    },
    {
      key: "chunk_count",
      header: "Chunks",
      align: "right",
      sortable: true,
      render: (source) => <Num value={source.chunk_count ?? 0} decimals={0} />,
    },
    {
      key: "processed_at",
      header: "Last extracted",
      sortable: true,
      render: (source) =>
        source.processed_at ? <RelativeTime value={source.processed_at} /> : <span className="muted">Not extracted</span>,
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Knowledge Centre</h1>
          <div className="sub">
            Upload, review and activate fraud recovery sources for graph and RAG retrieval. Only chunks an operator
            has approved — and that are still fresh — can reach a user.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={sources.reload} disabled={sources.loading}>
            {sources.loading && <span className="spinner" />}
            Refresh
          </button>
          {canWrite && (
            <button className="btn" onClick={() => void resyncAll()} disabled={syncing}>
              {syncing && <span className="spinner" />}
              Sync graph
            </button>
          )}
        </div>
      </div>

      <ErrorBanner error={sources.error} what="Failed to load knowledge sources" />

      {!sources.loading && (blocked.excluded > 0 || blocked.stale > 0) && (
        <Banner tone="warning">
          {blocked.excluded > 0 && (
            <>
              <strong>{blocked.excluded}</strong> {blocked.excluded === 1 ? "source is" : "sources are"} excluded from
              user-facing retrieval.{" "}
            </>
          )}
          {blocked.stale > 0 && (
            <>
              <strong>{blocked.stale}</strong> {blocked.stale === 1 ? "source has" : "sources have"} gone stale (no
              re-extraction in {STALE_DAYS} days, or a failed graph sync).{" "}
            </>
          )}
          <button
            className="btn sm"
            onClick={() => {
              setTab("sources");
              setFreshness(blocked.excluded > 0 ? "excluded" : "stale");
            }}
          >
            Show them
          </button>
        </Banner>
      )}

      <div className="knowledge">
        <div className="stack">
          <IngestPanel
            canWrite={canWrite}
            onIngested={() => {
              sources.reload();
              setReviewNonce((n) => n + 1);
            }}
          />
          <PreviewPanel />
        </div>

        <div className="stack">
          <Tabs
            tabs={[
              { id: "sources", label: `Sources (${rows.length})` },
              { id: "review", label: "Review queue" },
              { id: "trust", label: "Trust queue" },
            ]}
            active={tab}
            onChange={setTab}
          />

          {tab === "sources" && (
            <div className="card flush">
              <div className="card-head">
                <div className="grow" style={{ flex: 1 }}>
                  <Search value={query} onChange={setQuery} placeholder="Search sources" ariaLabel="Search sources" />
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 160 }} aria-label="Status">
                  <option value="all">Any status</option>
                  {SOURCE_STATUS_FILTERS.map((value) => (
                    <option key={value} value={value}>
                      {value.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
                <select
                  value={freshness}
                  onChange={(e) => setFreshness(e.target.value)}
                  style={{ width: 200 }}
                  aria-label="Freshness"
                >
                  {FRESHNESS_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Badge>{filtered.length} sources</Badge>
              </div>
              <Table
                columns={columns}
                rows={filtered}
                loading={sources.loading}
                empty={
                  <Empty
                    title="No knowledge sources found"
                    body={
                      rows.length
                        ? "No source matches this search or filter."
                        : "Upload a document, or paste an SOP, to start the corpus."
                    }
                    icon="▤"
                  />
                }
                onRowClick={(source) => navigate(`${BASE}/${source.id}`)}
              />
            </div>
          )}

          {tab === "review" && (
            <ReviewQueuePanel key={reviewNonce} canWrite={canWrite} onChanged={sources.reload} />
          )}

          {tab === "trust" && <TrustQueuePanel canWrite={canWrite} />}
        </div>
      </div>
    </div>
  );
}

// ============================================================ detail view

interface LinkForm {
  link_type: string;
  target_kind: string;
  target_key: string;
  label: string;
  relation: string;
}

const EMPTY_LINK_FORM: LinkForm = {
  link_type: "graph_node",
  target_kind: "risk_signal",
  target_key: "",
  label: "",
  relation: "",
};

const LINK_TYPES: Array<{ value: string; label: string }> = [
  { value: "graph_node", label: "Graph node" },
  { value: "wiki_source", label: "Wiki" },
  { value: "recovery_playbook", label: "Playbook" },
  { value: "guided_flow", label: "Flow" },
  { value: "flow_step", label: "Flow step" },
];

export function KnowledgeSourceDetailView({ sourceId }: { sourceId: string }) {
  const { can } = useSession();
  const canWrite = can("settings.chatbot", "write");
  const toast = useToast();

  const [tab, setTab] = useState("overview");
  const [chunks, setChunks] = useState<KnowledgeChunk[]>([]);
  const [candidates, setCandidates] = useState<KnowledgeCandidate[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [linkForms, setLinkForms] = useState<Record<string, LinkForm>>({});
  const [busyId, setBusyId] = useState("");
  const [reprocessing, setReprocessing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  const detail = useAsync<SourceDetail>(() => http.get<SourceDetail>(`${BASE}/sources/${sourceId}`), [sourceId]);

  useEffect(() => {
    if (!detail.data) return;
    setChunks(detail.data.chunks ?? []);
    setCandidates(detail.data.candidates ?? []);
  }, [detail.data]);

  // Trust-queue work raised about THIS source (OCR confidence, graph sync).
  const trust = useAsync<{ items: TrustItem[] }>(
    () => http.get<{ items: TrustItem[] }>(`/chatbot/trust-review-queue${qs({ status: "candidate" })}`),
    [sourceId]
  );
  const trustForSource = (trust.data?.items ?? []).filter((item) => item.target_id === sourceId);

  const source = detail.data?.source ?? null;
  const auditLogs = detail.data?.audit_logs ?? [];

  const approved = chunks.filter((chunk) => chunk.review_status === "approved");
  const staleApproved = approved.filter((chunk) => chunkFreshness(chunk.metadata).stale);
  const userFacing = approved.length - staleApproved.length;
  const pendingCandidates = candidates.filter(
    (candidate) => candidate.status === "candidate" || candidate.status === "needs_corroboration"
  ).length;

  const verdict = source ? sourceFreshness(source) : null;
  const graphSync = rec(rec(source?.metadata).knowledge_graph_sync);
  const extraction = rec(rec(source?.metadata).extraction);
  const graphIssues = listOf(graphSync.issues).map((issue) => rec(issue));

  const reload = () => {
    detail.reload();
    trust.reload();
  };

  const reprocess = async () => {
    setReprocessing(true);
    try {
      const result = await http.post<SourceWriteResult>(`${BASE}/sources/${sourceId}/reprocess`, {});
      toast.push(
        result.processing_enabled ? "Source queued for processing" : "Source marked for reprocessing",
        "good"
      );
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Failed to reprocess the source"), "critical");
      throw err;
    } finally {
      setReprocessing(false);
    }
  };

  const resync = async () => {
    setSyncing(true);
    try {
      await http.post<GraphResyncResult>(`${BASE}/graph/resync`, { source_id: sourceId });
      toast.push("Knowledge graph sync queued", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to resync the knowledge graph"), "critical");
    } finally {
      setSyncing(false);
      // The result is written onto the source either way, so re-read it.
      reload();
    }
  };

  const reviewChunk = async (chunk: KnowledgeChunk, next: string) => {
    setBusyId(chunk.id);
    try {
      const updated = await http.post<KnowledgeChunk>(`${BASE}/chunks/${chunk.id}/review`, {
        status: next,
        note: notes[chunk.id] || "",
      });
      setChunks((list) => list.map((item) => (item.id === chunk.id ? updated : item)));
      toast.push("Chunk review saved", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to review the chunk"), "critical");
      throw err;
    } finally {
      setBusyId("");
    }
  };

  const reviewCandidate = async (candidate: KnowledgeCandidate, next: string, mergeInto?: string) => {
    setBusyId(candidate.id);
    try {
      const updated = await http.post<KnowledgeCandidate>(`${BASE}/candidates/${candidate.id}/review`, {
        status: next,
        note: notes[candidate.id] || "",
        merge_into_candidate_id: mergeInto ?? "",
      });
      setCandidates((list) => list.map((item) => (item.id === candidate.id ? updated : item)));
      toast.push("Candidate review saved", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to review the candidate"), "critical");
      throw err;
    } finally {
      setBusyId("");
    }
  };

  const formFor = (id: string): LinkForm => linkForms[id] ?? EMPTY_LINK_FORM;
  const patchForm = (id: string, patch: Partial<LinkForm>) =>
    setLinkForms((forms) => ({ ...forms, [id]: { ...formFor(id), ...patch } }));

  const addLink = async (chunk: KnowledgeChunk) => {
    const form = formFor(chunk.id);
    if (!form.target_key.trim()) {
      toast.push("Enter a target key before linking", "warning");
      return;
    }
    setBusyId(chunk.id);
    try {
      const result = await http.post<ChunkLinkResult>(`${BASE}/chunks/${chunk.id}/links`, {
        link_type: form.link_type,
        target_kind: form.target_kind.trim(),
        target_key: form.target_key.trim(),
        label: form.label.trim(),
        relation: form.relation.trim(),
      });
      setChunks((list) => list.map((item) => (item.id === chunk.id ? result.chunk : item)));
      setLinkForms((forms) => ({ ...forms, [chunk.id]: { ...form, target_key: "", label: "", relation: "" } }));
      toast.push("Chunk link saved", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to link the chunk"), "critical");
    } finally {
      setBusyId("");
    }
  };

  const removeLink = async (chunk: KnowledgeChunk, link: ChunkLink) => {
    setBusyId(chunk.id);
    try {
      const result = await http.del<ChunkLinkResult>(`${BASE}/chunks/${chunk.id}/links/${link.id}`);
      setChunks((list) => list.map((item) => (item.id === chunk.id ? result.chunk : item)));
      toast.push("Chunk link removed", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to remove the link"), "critical");
      throw err;
    } finally {
      setBusyId("");
    }
  };

  const auditColumns: Column<AuditLogEntry>[] = [
    {
      key: "created_at",
      header: "Time",
      sortable: true,
      render: (log) => <span className="nowrap">{fmtDate(log.created_at)}</span>,
    },
    { key: "action", header: "Action", sortable: true, render: (log) => <Badge>{log.action}</Badge> },
    {
      key: "target_type",
      header: "Target",
      sortable: true,
      render: (log) => (
        <div>
          <div>{log.target_type}</div>
          <div className="mono muted" style={{ fontSize: 11 }}>
            {log.target_id ?? "—"}
          </div>
        </div>
      ),
    },
    { key: "diff", header: "Diff", render: (log) => <pre className="pre-block">{json(log.diff)}</pre> },
  ];

  if (detail.loading && !source) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <h1>Knowledge source</h1>
            <div className="sub">Review chunks, candidate graph facts and source provenance.</div>
          </div>
        </div>
        <SkeletonRows n={6} cols={3} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{source ? titleOf(source) : "Knowledge source"}</h1>
          <div className="sub">Review chunks, candidate graph facts and source provenance.</div>
        </div>
        <div className="page-actions">
          <a className="btn" href={BASE}>
            Back
          </a>
          <button className="btn" onClick={reload} disabled={detail.loading}>
            {detail.loading && <span className="spinner" />}
            Refresh
          </button>
          {canWrite && (
            <>
              <button
                className="btn"
                disabled={reprocessing}
                onClick={() =>
                  setPending({
                    title: "Reprocess this source",
                    // Reprocessing re-extracts and REPLACES the chunk set, which
                    // throws away every approval already recorded against it.
                    body: "Re-extraction replaces the current chunks and candidates. Every review decision recorded on them is lost and has to be made again.",
                    confirmLabel: "Reprocess",
                    danger: true,
                    run: reprocess,
                  })
                }
              >
                {reprocessing && <span className="spinner" />}
                Reprocess
              </button>
              <button className="btn" onClick={() => void resync()} disabled={syncing}>
                {syncing && <span className="spinner" />}
                Sync graph
              </button>
            </>
          )}
        </div>
      </div>

      <ErrorBanner error={detail.error} what="Failed to load the knowledge source" />

      {source && (
        <>
          {approved.length === 0 && chunks.length > 0 && (
            <Banner tone="warning">
              No chunk of this source is approved, so nothing here can reach a user. Approve the chunks that are
              correct on the <strong>Chunks</strong> tab.
            </Banner>
          )}
          {staleApproved.length > 0 && (
            <Banner tone="warning">
              <strong>{staleApproved.length}</strong> approved{" "}
              {staleApproved.length === 1 ? "chunk is a directory fact" : "chunks are directory facts"} with no
              validation date. Retrieval hides {staleApproved.length === 1 ? "it" : "them"} until someone revalidates,
              which is why a stale helpline number never reaches a user.
            </Banner>
          )}
          {source.extraction_error && <Banner tone="critical">{source.extraction_error}</Banner>}
          {text(graphSync.status) === "failed" && (
            <Banner tone="critical">
              Graph sync failed{text(graphSync.fallback_reason) ? `: ${text(graphSync.fallback_reason)}` : ""}. Graph
              answers still use the last synced copy of this source.
            </Banner>
          )}
        </>
      )}

      <Tabs
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "chunks", label: `Chunks (${chunks.length})` },
          { id: "candidates", label: `Candidates (${candidates.length})` },
          { id: "preview", label: "Retrieval preview" },
          { id: "graph", label: "Graph" },
          { id: "audit", label: "Audit" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {!source ? (
        <Empty title="Source unavailable" body="It may have been deleted, or you may not have access to it." />
      ) : (
        <div className="stack" style={{ marginTop: "var(--sp-4)" }}>
          {tab === "overview" && (
            <>
              <div className="grid c4">
                <StatTile label="Approved chunks" value={`${approved.length} / ${chunks.length}`} />
                <StatTile label="Reaching users" value={userFacing} />
                <StatTile label="Pending candidates" value={pendingCandidates} />
                <StatTile label="Chunks indexed" value={source.chunk_count ?? 0} />
              </div>

              <div className="grid c2">
                <div className="card">
                  <div className="section">
                    <h4>Source</h4>
                    <dl className="kv">
                      <dt>Status</dt>
                      <dd>
                        <StatusBadge status={source.status} />
                      </dd>
                      <dt>Review</dt>
                      <dd>
                        <StatusBadge status={source.review_status} />
                      </dd>
                      <dt>Retrieval</dt>
                      <dd>
                        {verdict && (
                          <>
                            <Badge tone={verdict.tone}>{verdict.label}</Badge>{" "}
                            <span className="muted">{verdict.why}</span>
                          </>
                        )}
                      </dd>
                      <dt>Type</dt>
                      <dd>{source.source_type}</dd>
                      <dt>File</dt>
                      <dd>
                        {source.file_name || "No file attached"}
                        {(source.mime_type || source.file_size) && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            {source.mime_type || "unknown type"}
                            {source.file_size ? ` · ${fmtBytes(source.file_size)}` : ""}
                          </div>
                        )}
                      </dd>
                      <dt>Reference</dt>
                      <dd className="mono">
                        {source.source_url || source.storage_key || source.file_name || source.id}
                      </dd>
                      <dt>Created</dt>
                      <dd>{fmtDate(source.created_at)}</dd>
                      <dt>Last extracted</dt>
                      <dd>{source.processed_at ? fmtDate(source.processed_at) : "Not extracted"}</dd>
                      <dt>Tags</dt>
                      <dd>
                        <div className="chips">
                          {listOf(source.tags).length === 0 ? (
                            <span className="muted">none</span>
                          ) : (
                            listOf(source.tags).map((tag, i) => <Tag key={i} label={String(tag)} />)
                          )}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>

                <div className="card">
                  <div className="section">
                    <h4>Extraction and OCR</h4>
                    <div className="chips">
                      {extraction.engine ? <Badge>{text(extraction.engine)}</Badge> : null}
                      {extraction.ocr_engine ? <Badge>{text(extraction.ocr_engine)}</Badge> : null}
                      {extraction.status ? <StatusBadge status={text(extraction.status)} /> : null}
                      {fmtPercent(extraction.ocr_confidence) && (
                        <Badge tone={(numeric(extraction.ocr_confidence) ?? 1) < 0.7 ? "warning" : "neutral"}>
                          OCR {fmtPercent(extraction.ocr_confidence)}
                        </Badge>
                      )}
                      {extraction.page_count ? <Badge>{text(extraction.page_count)} pages</Badge> : null}
                    </div>
                    {extraction.review_reason ? (
                      <p style={{ color: "var(--warning)", fontSize: 12, marginTop: "var(--sp-2)" }}>
                        {text(extraction.review_reason).replace(/_/g, " ")}
                      </p>
                    ) : null}
                    <dl className="kv" style={{ marginTop: "var(--sp-2)" }}>
                      <dt>Text hash</dt>
                      <dd className="mono">{source.raw_text_hash || "No extracted text hash yet"}</dd>
                      {extraction.error ? (
                        <>
                          <dt>Error</dt>
                          <dd style={{ color: "var(--critical)" }}>{text(extraction.error)}</dd>
                        </>
                      ) : null}
                    </dl>
                    <p className="muted" style={{ fontSize: 12, marginTop: "var(--sp-2)" }}>
                      The extracted body is never returned by the API. What OCR produced is what the chunks on the
                      Chunks tab contain — read them there before approving.
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h3>Knowledge graph sync</h3>
                  <StatusBadge status={text(graphSync.status) || "pending"} />
                  {graphSync.backend ? <Badge>{text(graphSync.backend)}</Badge> : null}
                  <span className="muted" style={{ fontSize: 12 }}>
                    {graphSync.synced_at ? fmtDate(text(graphSync.synced_at)) : "Not synced yet"}
                  </span>
                  {canWrite && (
                    <button className="btn sm" onClick={() => void resync()} disabled={syncing}>
                      {syncing && <span className="spinner" />}
                      Retry
                    </button>
                  )}
                </div>
                <div className="card-body">
                  <p className="muted" style={{ fontSize: 12 }}>
                    {numeric(graphSync.accepted_chunks) ?? 0} chunks / {numeric(graphSync.accepted_candidates) ?? 0}{" "}
                    candidates accepted
                    {numeric(graphSync.linked_similar) ? ` · ${numeric(graphSync.linked_similar)} similar links` : ""}
                    {numeric(graphSync.issue_count) ? ` · ${numeric(graphSync.issue_count)} issues` : ""}
                  </p>
                  {text(graphSync.fallback_reason) && (
                    <p style={{ color: "var(--critical)", fontSize: 12 }}>{text(graphSync.fallback_reason)}</p>
                  )}
                  {graphIssues.length > 0 && (
                    <div className="col">
                      {graphIssues.map((issue, i) => (
                        <div className="row" key={i}>
                          <Badge tone="warning">{text(issue.kind) || "graph_write"}</Badge>
                          <span style={{ fontSize: 12 }}>
                            {text(issue.message) || text(issue.reason) || "Review required"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="card flush">
                <div className="card-head">
                  <h3>Open trust review work</h3>
                  <Badge tone={trustForSource.length ? "warning" : "good"}>{trustForSource.length} open</Badge>
                </div>
                <div className="card-body">
                  <ErrorBanner error={trust.error} what="Failed to load the trust review queue" />
                  {trust.loading ? (
                    <SkeletonRows n={2} cols={2} />
                  ) : trustForSource.length === 0 ? (
                    <Empty title="No open trust items" body="Nothing was raised about this source." icon="✓" />
                  ) : (
                    trustForSource.map((item) => (
                      <div className="row" key={item.id} style={{ alignItems: "flex-start" }}>
                        <Badge tone={SEVERITY_TONE[item.severity] ?? "neutral"}>{item.severity}</Badge>
                        <div className="col" style={{ gap: 2 }}>
                          <span className="strong">{TRUST_KIND_LABEL[item.kind] ?? item.kind}</span>
                          <span className="muted" style={{ fontSize: 12 }}>
                            {item.reason || item.source}
                          </span>
                        </div>
                        <span className="spacer" />
                        <a className="btn sm" href="/chatbot/knowledge-centre">
                          Open queue
                        </a>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="card">
                <div className="section">
                  <h4>Provenance metadata</h4>
                  <pre className="pre-block">{json(source.metadata)}</pre>
                </div>
              </div>
            </>
          )}

          {tab === "chunks" && (
            <div className="card flush">
              <div className="card-head">
                <h3>Chunks</h3>
                <Badge tone={approved.length ? "good" : "warning"}>
                  {approved.length} approved · {userFacing} reaching users
                </Badge>
              </div>
              <div className="card-body">
                {chunks.length === 0 ? (
                  <Empty
                    title="No chunks yet"
                    body="Nothing has been extracted from this source, so there is nothing to review."
                  />
                ) : (
                  chunks.map((chunk) => {
                    const fresh = chunkFreshness(chunk.metadata);
                    const links = chunkLinks(chunk.metadata);
                    const form = formFor(chunk.id);
                    return (
                      <div className="card" key={chunk.id} style={{ marginBottom: "var(--sp-3)" }}>
                        <div className="chips">
                          <Badge>#{chunk.chunk_index}</Badge>
                          <StatusBadge status={chunk.review_status} />
                          {fresh.stale && (
                            <Badge
                              tone="warning"
                              title="Directory fact with no last_validated_on. Retrieval hides it even when approved."
                            >
                              stale — excluded
                            </Badge>
                          )}
                          {fresh.freshnessClass && !fresh.stale && (
                            <Badge title={fresh.lastValidated ? `Validated ${fresh.lastValidated}` : undefined}>
                              {fresh.freshnessClass}
                            </Badge>
                          )}
                          <span className="muted" style={{ fontSize: 12 }}>
                            {chunk.token_estimate} tokens
                          </span>
                          <span className="spacer" />
                          <ReviewButtons
                            actions={CHUNK_ACTIONS}
                            busy={busyId === chunk.id}
                            disabled={!canWrite}
                            onPick={(next) => void reviewChunk(chunk, next).catch(() => undefined)}
                          />
                        </div>

                        <p className="prewrap" style={{ marginTop: "var(--sp-3)" }}>
                          {chunk.content}
                        </p>

                        <div className="section" style={{ marginTop: "var(--sp-3)" }}>
                          <h4>Graph, wiki and playbook links</h4>
                          <div className="chips">
                            {links.length === 0 && <span className="muted">No graph/wiki/playbook links</span>}
                            {links.map((link) => (
                              <span className="tag" key={link.id}>
                                {link.link_type} / {link.target_key || link.target_id || "—"}
                                {canWrite && (
                                  <button
                                    aria-label={`Remove link ${link.link_type} ${link.target_key || link.target_id}`}
                                    disabled={busyId === chunk.id}
                                    onClick={() =>
                                      setPending({
                                        title: "Remove this link",
                                        body: `${link.link_type} → ${link.target_key || link.target_id} stops grounding this chunk in the graph.`,
                                        confirmLabel: "Remove link",
                                        danger: true,
                                        run: () => removeLink(chunk, link),
                                      })
                                    }
                                  >
                                    ✕
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>

                          {canWrite && (
                            <div className="filter-row" style={{ marginTop: "var(--sp-2)" }}>
                              <select
                                className="op"
                                value={form.link_type}
                                aria-label="Link type"
                                onChange={(e) => patchForm(chunk.id, { link_type: e.target.value })}
                              >
                                {LINK_TYPES.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                value={form.target_kind}
                                placeholder="Kind"
                                aria-label="Target kind"
                                onChange={(e) => patchForm(chunk.id, { target_kind: e.target.value })}
                              />
                              <input
                                type="text"
                                value={form.target_key}
                                placeholder="Key or id"
                                aria-label="Target key"
                                onChange={(e) => patchForm(chunk.id, { target_key: e.target.value })}
                              />
                              <input
                                type="text"
                                value={form.label}
                                placeholder="Label"
                                aria-label="Link label"
                                onChange={(e) => patchForm(chunk.id, { label: e.target.value })}
                              />
                              <button
                                className="btn sm"
                                disabled={busyId === chunk.id}
                                onClick={() => void addLink(chunk)}
                              >
                                Link
                              </button>
                            </div>
                          )}
                        </div>

                        <NoteBox
                          value={notes[chunk.id] ?? ""}
                          disabled={!canWrite}
                          onChange={(v) => setNotes((n) => ({ ...n, [chunk.id]: v }))}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {tab === "candidates" && (
            <div className="card flush">
              <div className="card-head">
                <h3>Candidate graph facts</h3>
                <Badge tone={pendingCandidates ? "warning" : "good"}>{pendingCandidates} pending</Badge>
              </div>
              <div className="card-body">
                {candidates.length === 0 ? (
                  <Empty title="No candidates yet" body="The extractor proposed no graph facts for this source." />
                ) : (
                  candidates.map((candidate) => {
                    const duplicates = duplicateIds(candidate.payload);
                    const target = mergeTarget(candidate.id, candidate.payload);
                    return (
                      <div className="card" key={candidate.id} style={{ marginBottom: "var(--sp-3)" }}>
                        <div className="chips">
                          <Badge>{candidate.candidate_type}</Badge>
                          <StatusBadge status={candidate.status} />
                          {duplicates.length > 1 && <Badge tone="info">{duplicates.length} duplicates</Badge>}
                          {candidateStale(candidate.payload) && (
                            <Badge
                              tone="warning"
                              title="Directory fact with no validation date; the graph hides it from retrieval until it is validated."
                            >
                              stale directory fact
                            </Badge>
                          )}
                          <span className="muted" style={{ fontSize: 12 }}>
                            confidence {Number(candidate.confidence || 0).toFixed(2)}
                          </span>
                          <span className="spacer" />
                          <ReviewButtons
                            actions={CANDIDATE_ACTIONS}
                            busy={busyId === candidate.id}
                            disabled={!canWrite}
                            onPick={(next) => void reviewCandidate(candidate, next).catch(() => undefined)}
                          />
                          {target && canWrite && (
                            <button
                              className="btn sm"
                              disabled={busyId === candidate.id}
                              onClick={() =>
                                setPending({
                                  title: "Merge duplicate candidate",
                                  body: `This candidate is folded into ${target} and stops being reviewed on its own. The merge is recorded in the payload and cannot be undone from the console.`,
                                  confirmLabel: "Merge",
                                  run: async () => {
                                    await reviewCandidate(candidate, "merged", target);
                                  },
                                })
                              }
                            >
                              Merge duplicate
                            </button>
                          )}
                        </div>
                        <p className="strong" style={{ marginTop: "var(--sp-2)" }}>
                          {candidate.target_kind || "candidate"} / {candidate.target_key || "unkeyed"}
                        </p>
                        {candidate.reviewer_note && (
                          <p className="muted" style={{ fontSize: 12 }}>
                            note: {candidate.reviewer_note}
                          </p>
                        )}
                        <pre className="pre-block">{json(candidate.payload)}</pre>
                        <NoteBox
                          value={notes[candidate.id] ?? ""}
                          disabled={!canWrite}
                          onChange={(v) => setNotes((n) => ({ ...n, [candidate.id]: v }))}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {tab === "preview" && <PreviewPanel highlightId={sourceId} />}

          {tab === "graph" && (
            <div className="card">
              <div className="section">
                <h4>Graph linking</h4>
                <p>
                  Approved candidates are eligible for graph linking. Detailed graph node merge automation is queued
                  for the next intelligence batch.
                </p>
                <p className="muted" style={{ fontSize: 12 }}>
                  Only approved chunks and approved candidates are ever pushed to the graph, and a directory fact
                  without a validation date stays invisible to retrieval until someone revalidates it.
                </p>
              </div>
            </div>
          )}

          {tab === "audit" && (
            <div className="card flush">
              <div className="card-head">
                <h3>Audit trail</h3>
                <Badge>{auditLogs.length} events</Badge>
              </div>
              <Table
                columns={auditColumns}
                rows={auditLogs}
                loading={detail.loading}
                empty="No audit events yet"
              />
            </div>
          )}
        </div>
      )}

      <ConfirmHost pending={pending} onClose={() => setPending(null)} />
    </div>
  );
}
